import React, { useEffect, useMemo, useRef, useState } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Box,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DesignServicesOutlinedIcon from "@mui/icons-material/DesignServicesOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import TextureOutlinedIcon from "@mui/icons-material/TextureOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import {
  entriesForCompassColumn,
  resolveCompassColumnPath,
  searchStatusLabel,
  taskStatusColor,
  taskStatusLabel,
  rapportStatusLabel,
} from "./skillAgentTaskTree";
import {
  ORG_BRANDS,
  normalizeOrgBrand,
  otherOrgBrand,
} from "./skillAgentOrgBrand";
import {
  favoriteFromCompassNode,
  favoriteKey,
  isCompassFavorite,
} from "./skillAgentCompassFavorites";

/** Misma base que la cabecera de filtros del drawer Compás. */
const COL_WIDTH = 320;

function accentForKind(kind) {
  switch (kind) {
    case "producciones":
      return "#bf360c";
    case "clientInfos":
      return "#0277bd";
    case "rapportInterpret":
      return "#6a1b9a";
    case "rapportTiles":
    case "rapports":
    case "moodboards":
      return "#6a1b9a";
    case "resumenes":
      return "#1565c0";
    case "fichasTecnicas":
      return "#00695c";
    case "searchs":
      return "#00897b";
    case "sketches":
      return "#5e35b1";
    case "disenos":
      return "#1565c0";
    case "meetings":
      return "#e65100";
    case "meeting":
      return "#546e7a";
    case "season":
      return "#00897b";
    case "client":
      return "#546e7a";
    case "project":
    case "orgBranch":
      return "#455a64";
    default:
      return "#607d8b";
  }
}

function IconForKind({ kind, sx }) {
  const props = { sx: { fontSize: 16, ...sx } };
  switch (kind) {
    case "producciones":
      return <PrecisionManufacturingOutlinedIcon {...props} />;
    case "clientInfos":
      return <InfoOutlinedIcon {...props} />;
    case "rapportInterpret":
      return <AutoFixHighOutlinedIcon {...props} />;
    case "rapportTiles":
      return <GridViewOutlinedIcon {...props} />;
    case "rapports":
      return <TextureOutlinedIcon {...props} />;
    case "resumenes":
      return <SummarizeOutlinedIcon {...props} />;
    case "fichasTecnicas":
      return <DescriptionOutlinedIcon {...props} />;
    case "moodboards":
      return <DashboardCustomizeOutlinedIcon {...props} />;
    case "searchs":
      return <TravelExploreOutlinedIcon {...props} />;
    case "disenos":
    case "sketches":
    case "project":
      return <DesignServicesOutlinedIcon {...props} />;
    case "meetings":
    case "season":
      return <CalendarMonthOutlinedIcon {...props} />;
    case "meeting":
      return <GroupsOutlinedIcon {...props} />;
    case "orgBranch":
    case "client":
      return <BusinessOutlinedIcon {...props} />;
    default:
      return <ChevronRightIcon {...props} />;
  }
}

function entryCount(entry) {
  if (entry.entryKind === "leaf") return null;
  const n = entry.node;
  if (!n) return Number(entry.count) || 0;
  if (n.kind === "meetings") return (n.children || []).length;
  if (n.kind === "searchs") return (n.searches || []).length;
  if (n.kind === "moodboards") return (n.moodboards || []).length;
  if (n.kind === "fichasTecnicas")
    return (n.fichasTecnicas || n.moodboards || []).length;
  if (n.kind === "resumenes") return (n.resumenes || n.moodboards || []).length;
  if (n.kind === "rapportTiles" || n.kind === "rapportInterpret")
    return (n.rapports || []).length;
  if (n.kind === "clientInfos" || n.kind === "producciones")
    return (n.clientInfos || []).length;
  if (n.kind === "disenos") return (n.tasks || []).length;
  if (n.kind === "sketches") return (n.moodboards || n.aiDesigns || []).length;
  if (n.kind === "project") {
    return (n.orgBranches || []).reduce(
      (sum, b) => sum + (Number(b.count) || 0),
      0,
    );
  }
  if (n.kind === "orgBranch" || n.kind === "meeting") {
    return Number(n.count) || 0;
  }
  if (n.children?.length) {
    return n.children.reduce((sum, c) => sum + (Number(c.count) || 0), 0);
  }
  return Number(n.count) || 0;
}

function canHubAdd(node) {
  return node?.kind === "meeting" || node?.kind === "orgBranch";
}

function isLibraryHub(kind) {
  return [
    "searchs",
    "moodboards",
    "fichasTecnicas",
    "resumenes",
    "disenos",
    "sketches",
    "rapports",
    "rapportTiles",
    "rapportInterpret",
    "clientInfos",
    "producciones",
    "meetings",
  ].includes(kind);
}

/**
 * Navegador Compás en columnas (Finder): pathIds = nodos seleccionados por nivel.
 */
export default function SkillAgentCompassColumns({
  tree = [],
  pathIds = [],
  onPathChange,
  selectedId,
  selectedSearchId,
  selectedMoodboardId,
  selectedRapportId,
  selectedClientInfoId,
  selectedMeetingId,
  selectedAiDesignId,
  selectedProjectId,
  onSelectTask,
  onSelectSearch,
  onSelectMoodboard,
  onSelectResumen,
  onSelectRapport,
  onSelectClientInfo,
  onSelectAiDesign,
  onSelectProject,
  onNewSearch,
  onNewMoodboard,
  onNewResumen,
  onNewFicha,
  onNewRapport,
  onNewClientInfo,
  onNewProduccion,
  onNewTask,
  onNewMeeting,
  onOpenMeeting,
  onHubCreate,
  onEditProjectSeason,
  onDeleteProject,
  onMoveOrgBrand,
  onExpandProject,
  favorites = [],
  onToggleFavorite,
  onOpenFavorite,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const scrollerRef = useRef(null);
  const [moveMenu, setMoveMenu] = useState(null);
  const [projectMenu, setProjectMenu] = useState(null);
  const favAccent = "#f9a825";

  const pathNodes = useMemo(
    () => resolveCompassColumnPath(tree, pathIds),
    [tree, pathIds],
  );

  // Validar path si el árbol cambió (filtro / rebuild).
  useEffect(() => {
    if (!pathIds?.length) return;
    if (pathNodes.length === pathIds.length) return;
    onPathChange?.(pathNodes.map((n) => n.id));
  }, [pathIds, pathNodes, onPathChange]);

  const columns = useMemo(() => {
    const cols = [
      { parent: null, entries: entriesForCompassColumn(null, tree) },
    ];
    for (const node of pathNodes) {
      cols.push({
        parent: node,
        entries: entriesForCompassColumn(node, tree),
      });
    }
    return cols;
  }, [tree, pathNodes]);

  useEffect(() => {
    if (isMobile) return;
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
  }, [columns.length, isMobile, pathIds]);

  const visibleColumns = isMobile
    ? columns.slice(Math.max(0, columns.length - 1))
    : columns;

  const setPath = (nextIds) => {
    onPathChange?.(nextIds.map(String).filter(Boolean));
  };

  const drillTo = (columnIndex, node) => {
    if (!node?.id) return;
    const next = pathIds.slice(0, columnIndex).concat(String(node.id));
    setPath(next);
    if (node.kind === "project") {
      onExpandProject?.(node);
      onSelectProject?.(node);
    }
    // Meeting: click = abrir columna a la derecha; ojo = abrir sesión.
  };

  const goBack = () => {
    if (pathIds.length === 0) return;
    setPath(pathIds.slice(0, -1));
  };

  const openMoveMenu = (e, kind, item) => {
    e.preventDefault();
    e.stopPropagation();
    setMoveMenu({
      mouseX: e.clientX + 2,
      mouseY: e.clientY - 6,
      kind,
      item,
    });
  };

  const isLeafSelected = (entry) => {
    if (entry.entryKind !== "leaf") return false;
    const id = Number(entry.item?.id);
    switch (entry.leafKind) {
      case "task":
        return Number(selectedId) === id;
      case "search":
        return Number(selectedSearchId) === id;
      case "moodboard":
        return Number(selectedMoodboardId) === id;
      case "aiDesign":
        return (
          Number(selectedAiDesignId) === id ||
          Number(selectedMoodboardId) === id ||
          Number(selectedId) === id
        );
      case "resumen":
        return Number(selectedMoodboardId) === id;
      case "rapport":
        return Number(selectedRapportId) === id;
      case "clientInfo":
      case "produccion":
        return Number(selectedClientInfoId) === id;
      default:
        return false;
    }
  };

  const isNodeSelectedInPath = (columnIndex, nodeId) =>
    String(pathIds[columnIndex] || "") === String(nodeId);

  const handleLeafClick = (entry) => {
    const item = entry.item;
    if (!item) return;
    switch (entry.leafKind) {
      case "task":
        onSelectTask?.(item.id);
        break;
      case "search":
        onSelectSearch?.(item.id);
        break;
      case "moodboard":
        onSelectMoodboard?.(item.id);
        break;
      case "resumen":
        onSelectResumen?.(item.id);
        break;
      case "rapport":
        onSelectRapport?.(item.id);
        break;
      case "clientInfo":
      case "produccion":
        onSelectClientInfo?.(item.id);
        break;
      case "aiDesign":
        onSelectAiDesign?.(item);
        break;
      default:
        break;
    }
  };

  const leafStatus = (entry) => {
    const item = entry.item;
    if (!item) return null;
    if (entry.leafKind === "task") {
      return (
        <Chip
          size="small"
          label={taskStatusLabel(item.Status)}
          color={taskStatusColor(item.Status)}
          variant="outlined"
          sx={{
            height: 18,
            fontSize: "0.62rem",
            "& .MuiChip-label": { px: 0.55 },
          }}
        />
      );
    }
    if (entry.leafKind === "search") {
      return (
        <Chip
          size="small"
          label={searchStatusLabel(item.Status)}
          variant="outlined"
          sx={{
            height: 18,
            fontSize: "0.62rem",
            "& .MuiChip-label": { px: 0.55 },
          }}
        />
      );
    }
    if (entry.leafKind === "rapport") {
      return (
        <Chip
          size="small"
          label={rapportStatusLabel(item.Status)}
          variant="outlined"
          sx={{
            height: 18,
            fontSize: "0.62rem",
            "& .MuiChip-label": { px: 0.55 },
          }}
        />
      );
    }
    return null;
  };

  const addBtnSx = (tone = "#00897b") => ({
    width: 26,
    height: 26,
    flexShrink: 0,
    color: tone,
    border: "1px solid",
    borderColor: alpha(tone, 0.35),
    borderRadius: 1.25,
    bgcolor: alpha(tone, 0.08),
    "&:hover": { bgcolor: alpha(tone, 0.16) },
  });

  const renderAddForParent = (parent) => {
    if (!parent) return null;
    const kind = parent.kind;
    const tone = accentForKind(kind);
    if (kind === "project" || canHubAdd(parent)) {
      return (
        <Tooltip
          title={t("skillAgent.tree.ariaHubCreate", {
            defaultValue: "Crear en este hub",
          })}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onHubCreate?.(parent);
            }}
            aria-label={t("skillAgent.tree.ariaHubCreate", {
              defaultValue: "Crear en este hub",
            })}
            sx={addBtnSx(tone)}
          >
            <AddOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      );
    }
    if (kind === "meetings") {
      return (
        <Tooltip
          title={t("skillAgent.tree.ariaNewMeeting", {
            defaultValue: "Nueva reunión",
          })}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onNewMeeting?.(parent);
            }}
            sx={addBtnSx(tone)}
          >
            <AddOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      );
    }
    if (isLibraryHub(kind) && kind !== "meetings" && kind !== "rapports") {
      return (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (kind === "disenos" || kind === "sketches") onNewTask?.(parent);
            else if (kind === "searchs") onNewSearch?.(parent);
            else if (kind === "moodboards") onNewMoodboard?.(parent);
            else if (kind === "fichasTecnicas") onNewFicha?.(parent);
            else if (kind === "resumenes") onNewResumen?.(parent);
            else if (kind === "producciones") onNewProduccion?.(parent);
            else if (kind === "clientInfos") onNewClientInfo?.(parent);
            else if (kind === "rapportTiles") onNewRapport?.(parent, "tartan");
            else if (kind === "rapportInterpret")
              onNewRapport?.(parent, "seamless");
          }}
          sx={addBtnSx(tone)}
        >
          <AddOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      );
    }
    return null;
  };

  const renderRowAdd = (node) => {
    if (!node) return null;
    const kind = node.kind;
    if (
      kind === "project" ||
      kind === "orgBranch" ||
      kind === "meeting" ||
      kind === "meetings" ||
      (isLibraryHub(kind) && kind !== "rapports")
    ) {
      return (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{ display: "inline-flex", flexShrink: 0 }}
        >
          {renderAddForParent(node)}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {isMobile && pathIds.length > 0 ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{
            px: 1,
            py: 0.65,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <IconButton size="small" onClick={goBack} aria-label="Back">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="caption"
            fontWeight={700}
            noWrap
            sx={{ flex: 1, minWidth: 0 }}
          >
            {pathNodes[pathNodes.length - 1]?.label || ""}
          </Typography>
          {renderAddForParent(pathNodes[pathNodes.length - 1])}
        </Stack>
      ) : null}

      <Box
        ref={scrollerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "row",
          overflowX: isMobile ? "hidden" : "auto",
          overflowY: "hidden",
        }}
      >
        {visibleColumns.map((col, visIdx) => {
          const columnIndex = isMobile ? columns.length - 1 : visIdx;
          const accent = accentForKind(col.parent?.kind);
          return (
            <Box
              key={col.parent?.id || "root"}
              sx={{
                width: isMobile ? "100%" : COL_WIDTH,
                minWidth: isMobile ? "100%" : COL_WIDTH,
                maxWidth: isMobile ? "100%" : COL_WIDTH,
                borderRight: isMobile ? "none" : "1px solid",
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                bgcolor: isDark
                  ? alpha(theme.palette.background.paper, 0.35)
                  : alpha("#fff", columnIndex % 2 ? 0.55 : 0.9),
              }}
            >
              {!isMobile && col.parent ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{
                    px: 1,
                    py: 0.7,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    minHeight: 34,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      color: accent,
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <IconForKind kind={col.parent.kind} />
                  </Box>
                  <Typography
                    variant="caption"
                    fontWeight={800}
                    noWrap
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      letterSpacing: 0.02,
                      textTransform:
                        col.parent.kind === "orgBranch" ? "none" : "uppercase",
                      fontSize: "0.65rem",
                      color: accent,
                    }}
                  >
                    {col.parent.label}
                  </Typography>
                  {renderAddForParent(col.parent)}
                </Stack>
              ) : null}

              <Box sx={{ flex: 1, overflowY: "auto", py: 0.4, px: 0.35 }}>
                {columnIndex === 0 && (favorites || []).length > 0 ? (
                  <Box sx={{ mb: 0.75, pb: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        px: 0.85,
                        pt: 0.35,
                        pb: 0.55,
                        fontWeight: 800,
                        fontSize: "0.62rem",
                        letterSpacing: 0.06,
                        textTransform: "uppercase",
                        color: favAccent,
                      }}
                    >
                      {t("skillAgent.tree.favorites", {
                        defaultValue: "Favoritos",
                      })}
                    </Typography>
                    {favorites.map((fav) => (
                      <Box
                        key={favoriteKey(fav)}
                        onClick={() => onOpenFavorite?.(fav)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.55,
                          minHeight: 32,
                          px: 0.85,
                          py: 0.3,
                          mb: 0.15,
                          borderRadius: 1.25,
                          cursor: "pointer",
                          "&:hover": {
                            bgcolor: alpha(favAccent, 0.12),
                            "& .fav-remove": { opacity: 1 },
                          },
                        }}
                      >
                        <StarOutlinedIcon
                          sx={{ fontSize: 15, color: favAccent, flexShrink: 0 }}
                        />
                        <Typography
                          variant="body2"
                          noWrap
                          fontWeight={600}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: "0.8rem",
                          }}
                        >
                          {fav.label}
                        </Typography>
                        <IconButton
                          size="small"
                          className="fav-remove"
                          aria-label={t("skillAgent.tree.removeFavorite", {
                            defaultValue: "Quitar de Favoritos",
                          })}
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleFavorite?.(fav);
                          }}
                          sx={{
                            width: 22,
                            height: 22,
                            opacity: 0,
                            flexShrink: 0,
                            color: favAccent,
                          }}
                        >
                          <StarOutlinedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                    <Box
                      sx={{
                        mx: 0.85,
                        mt: 0.45,
                        mb: 0.35,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                  </Box>
                ) : null}
                {col.entries.length === 0 ? (
                  <Box sx={{ px: 1, py: 2, textAlign: "center" }}>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ display: "block", mb: 1 }}
                    >
                      {col.parent?.kind === "project" && !col.parent?.loaded
                        ? t("skillAgent.tree.loadingProject", {
                            defaultValue: "Cargando…",
                          })
                        : t("skillAgent.tree.columnEmpty", {
                            defaultValue: "Vacío",
                          })}
                    </Typography>
                    {col.parent &&
                    (col.parent.kind === "project" ||
                      col.parent.kind === "orgBranch" ||
                      col.parent.kind === "meeting") &&
                    col.parent.loaded !== false ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 0.75,
                          alignItems: "center",
                        }}
                      >
                        {renderAddForParent(col.parent)}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={700}
                        >
                          {t("skillAgent.tree.createHere", {
                            defaultValue: "Crear aquí",
                          })}
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>
                ) : (
                  col.entries.map((entry) => {
                    if (entry.entryKind === "leaf") {
                      const selected = isLeafSelected(entry);
                      const tone = accentForKind(
                        entry.leafKind === "aiDesign"
                          ? "sketches"
                          : entry.leafKind === "task"
                            ? "disenos"
                            : entry.leafKind === "search"
                              ? "searchs"
                              : entry.leafKind === "resumen"
                                ? "resumenes"
                                : entry.leafKind === "rapport"
                                  ? "rapports"
                                  : entry.leafKind === "produccion"
                                    ? "producciones"
                                    : entry.leafKind === "clientInfo"
                                      ? "clientInfos"
                                      : "moodboards",
                      );
                      return (
                        <Box
                          key={entry.id}
                          data-tree-id={entry.id}
                          onClick={() => handleLeafClick(entry)}
                          onContextMenu={(e) =>
                            openMoveMenu(
                              e,
                              entry.leafKind === "aiDesign"
                                ? "moodboard"
                                : entry.leafKind === "produccion"
                                  ? "clientInfo"
                                  : entry.leafKind === "resumen"
                                    ? "moodboard"
                                    : entry.leafKind,
                              entry.item,
                            )
                          }
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.6,
                            minHeight: 32,
                            px: 0.85,
                            py: 0.35,
                            mb: 0.2,
                            borderRadius: 1.25,
                            cursor: "pointer",
                            border: "1px solid",
                            borderColor: selected
                              ? alpha(tone, 0.4)
                              : "transparent",
                            bgcolor: selected
                              ? alpha(tone, 0.12)
                              : "transparent",
                            "&:hover": {
                              bgcolor: alpha(tone, selected ? 0.16 : 0.07),
                              "& .leaf-move": { opacity: 1 },
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={selected ? 700 : 500}
                            noWrap
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              fontSize: "0.8125rem",
                            }}
                          >
                            {entry.label}
                          </Typography>
                          {leafStatus(entry)}
                          <Tooltip
                            title={t("skillAgent.tree.moveToBrand", {
                              brand: otherOrgBrand(
                                entry.item?.OrgBrand || entry.item?.orgBrand,
                              ),
                              defaultValue: `Mover a ${otherOrgBrand(
                                entry.item?.OrgBrand || entry.item?.orgBrand,
                              )}`,
                            })}
                          >
                            <IconButton
                              size="small"
                              className="leaf-move"
                              onClick={(e) => {
                                e.stopPropagation();
                                onMoveOrgBrand?.(
                                  entry.leafKind === "aiDesign"
                                    ? "moodboard"
                                    : entry.leafKind === "produccion"
                                      ? "clientInfo"
                                      : entry.leafKind === "resumen"
                                        ? "moodboard"
                                        : entry.leafKind,
                                  entry.item,
                                  otherOrgBrand(
                                    entry.item?.OrgBrand ||
                                      entry.item?.orgBrand,
                                  ),
                                );
                              }}
                              sx={{
                                width: 22,
                                height: 22,
                                opacity: 0,
                                flexShrink: 0,
                              }}
                            >
                              <SwapHorizOutlinedIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      );
                    }

                    const node = entry.node;
                    const kind = node.kind;
                    const tone = accentForKind(kind);
                    const inPath = isNodeSelectedInPath(columnIndex, node.id);
                    const meetingSelected =
                      kind === "meeting" &&
                      Number(selectedMeetingId) > 0 &&
                      (Number(node.MeetingSessionId) ===
                        Number(selectedMeetingId) ||
                        Number(node.meeting?.id) === Number(selectedMeetingId));
                    const projectSelected =
                      kind === "project" &&
                      Number(selectedProjectId) > 0 &&
                      Number(node.ProjectId || node.projectId) ===
                        Number(selectedProjectId);
                    const selected =
                      inPath || meetingSelected || projectSelected;
                    const count = entryCount(entry);
                    const hasChildren =
                      entriesForCompassColumn(node, tree).length > 0 ||
                      (kind === "project" && !node.loaded);
                    const favEntry = favoriteFromCompassNode(node);
                    const isFav =
                      favEntry &&
                      isCompassFavorite(favorites, favEntry.kind, favEntry.id);

                    return (
                      <Box
                        key={entry.id}
                        data-tree-id={node.id}
                        onClick={() => drillTo(columnIndex, node)}
                        onContextMenu={
                          kind === "meeting"
                            ? (e) =>
                                openMoveMenu(e, "meeting", {
                                  id: node.MeetingSessionId || node.meeting?.id,
                                  OrgBrand: node.OrgBrand || node.orgBrand,
                                  orgBrand: node.orgBrand || node.OrgBrand,
                                  ProjectId: node.ProjectId || node.projectId,
                                  ...(node.meeting || {}),
                                  __favNode: node,
                                })
                            : kind === "project"
                              ? (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setProjectMenu({
                                    mouseX: e.clientX + 2,
                                    mouseY: e.clientY - 6,
                                    node,
                                  });
                                }
                              : undefined
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.65,
                          minHeight: 34,
                          px: 0.85,
                          py: 0.4,
                          mb: 0.2,
                          borderRadius: 1.35,
                          cursor: "pointer",
                          border: "1px solid",
                          borderColor: selected
                            ? alpha(tone, 0.4)
                            : "transparent",
                          bgcolor: selected
                            ? alpha(tone, isDark ? 0.22 : 0.12)
                            : "transparent",
                          "&:hover": {
                            bgcolor: alpha(tone, selected ? 0.26 : 0.08),
                            "& .meeting-open-eye": { opacity: 1 },
                            "& .row-fav-star": { opacity: 1 },
                            "& .project-season-edit": { opacity: 1 },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            color: tone,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                          }}
                        >
                          <IconForKind kind={kind} />
                        </Box>
                        <Typography
                          variant="body2"
                          fontWeight={selected ? 800 : 600}
                          noWrap
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: "0.8125rem",
                            lineHeight: 1.25,
                          }}
                        >
                          {entry.label}
                        </Typography>
                        {kind === "project" &&
                        !String(node.Temporada || "").trim() ? (
                          <Tooltip
                            title={t(
                              "skillAgent.tree.editProjectMissingSeason",
                              {
                                defaultValue: "Falta temporada — recolocar",
                              },
                            )}
                          >
                            <IconButton
                              size="small"
                              className="project-season-edit"
                              aria-label={t(
                                "skillAgent.tree.editProjectMissingSeason",
                                {
                                  defaultValue: "Falta temporada — recolocar",
                                },
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditProjectSeason?.(node);
                              }}
                              sx={{
                                width: 26,
                                height: 26,
                                flexShrink: 0,
                                opacity: 1,
                                color: "#00897b",
                                bgcolor: alpha("#00897b", 0.14),
                                border: "1px solid",
                                borderColor: alpha("#00897b", 0.45),
                                borderRadius: 1.25,
                                transition:
                                  "background-color 0.12s, transform 0.12s",
                                "&:hover": {
                                  bgcolor: "#00897b",
                                  color: "#fff",
                                  transform: "scale(1.05)",
                                },
                              }}
                            >
                              <CalendarMonthOutlinedIcon
                                sx={{ fontSize: 15 }}
                              />
                            </IconButton>
                          </Tooltip>
                        ) : kind === "project" ? (
                          <Tooltip
                            title={t("skillAgent.tree.editProject", {
                              defaultValue: "Editar proyecto",
                            })}
                          >
                            <IconButton
                              size="small"
                              className="project-season-edit"
                              aria-label={t("skillAgent.tree.editProject", {
                                defaultValue: "Editar proyecto",
                              })}
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditProjectSeason?.(node);
                              }}
                              sx={{
                                width: 22,
                                height: 22,
                                flexShrink: 0,
                                opacity: 0,
                                color: tone,
                                transition: "opacity 0.12s",
                                "&:hover": {
                                  bgcolor: alpha(tone, 0.12),
                                },
                              }}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {favEntry ? (
                          <Tooltip
                            title={
                              isFav
                                ? t("skillAgent.tree.removeFavorite", {
                                    defaultValue: "Quitar de Favoritos",
                                  })
                                : t("skillAgent.tree.addFavorite", {
                                    defaultValue: "Añadir a Favoritos",
                                  })
                            }
                          >
                            <IconButton
                              size="small"
                              className="row-fav-star"
                              aria-label={
                                isFav
                                  ? t("skillAgent.tree.removeFavorite", {
                                      defaultValue: "Quitar de Favoritos",
                                    })
                                  : t("skillAgent.tree.addFavorite", {
                                      defaultValue: "Añadir a Favoritos",
                                    })
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                onToggleFavorite?.(favEntry);
                              }}
                              sx={{
                                width: 22,
                                height: 22,
                                opacity: isFav ? 1 : 0,
                                flexShrink: 0,
                                color: favAccent,
                                transition: "opacity 0.12s",
                                "&:hover": {
                                  bgcolor: alpha(favAccent, 0.14),
                                },
                              }}
                            >
                              {isFav ? (
                                <StarOutlinedIcon sx={{ fontSize: 15 }} />
                              ) : (
                                <StarBorderOutlinedIcon sx={{ fontSize: 15 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {kind === "meeting" ? (
                          <Tooltip
                            title={t("skillAgent.tree.openMeeting", {
                              defaultValue: "Abrir reunión",
                            })}
                          >
                            <IconButton
                              size="small"
                              className="meeting-open-eye"
                              aria-label={t("skillAgent.tree.openMeeting", {
                                defaultValue: "Abrir reunión",
                              })}
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenMeeting?.(node);
                              }}
                              sx={{
                                width: 30,
                                height: 30,
                                flexShrink: 0,
                                color: meetingSelected ? "#fff" : "#e65100",
                                bgcolor: meetingSelected
                                  ? "#e65100"
                                  : alpha("#e65100", 0.14),
                                border: "1px solid",
                                borderColor: alpha("#e65100", 0.55),
                                borderRadius: 1.5,
                                opacity: 1,
                                transition:
                                  "background-color 0.12s, color 0.12s, transform 0.12s",
                                "&:hover": {
                                  bgcolor: "#e65100",
                                  color: "#fff",
                                  borderColor: "#e65100",
                                  transform: "scale(1.06)",
                                },
                              }}
                            >
                              <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {renderRowAdd(node)}
                        {count != null && count > 0 ? (
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.65rem",
                              color: alpha(tone, 0.85),
                              flexShrink: 0,
                            }}
                          >
                            {count}
                          </Typography>
                        ) : null}
                        {hasChildren ? (
                          <ChevronRightIcon
                            sx={{
                              fontSize: 16,
                              color: "text.disabled",
                              flexShrink: 0,
                            }}
                          />
                        ) : null}
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Menu
        open={Boolean(moveMenu)}
        onClose={() => setMoveMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          moveMenu ? { top: moveMenu.mouseY, left: moveMenu.mouseX } : undefined
        }
        onClick={(e) => e.stopPropagation()}
      >
        {moveMenu?.kind === "meeting" && moveMenu?.item?.__favNode
          ? (() => {
              const entry = favoriteFromCompassNode(moveMenu.item.__favNode);
              const starred =
                entry && isCompassFavorite(favorites, entry.kind, entry.id);
              return (
                <MenuItem
                  onClick={() => {
                    setMoveMenu(null);
                    if (entry) onToggleFavorite?.(entry);
                  }}
                >
                  <ListItemIcon>
                    {starred ? (
                      <StarOutlinedIcon
                        fontSize="small"
                        sx={{ color: favAccent }}
                      />
                    ) : (
                      <StarBorderOutlinedIcon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      starred
                        ? t("skillAgent.tree.removeFavorite", {
                            defaultValue: "Quitar de Favoritos",
                          })
                        : t("skillAgent.tree.addFavorite", {
                            defaultValue: "Añadir a Favoritos",
                          })
                    }
                  />
                </MenuItem>
              );
            })()
          : null}
        {ORG_BRANDS.map((brand) => {
          const current = normalizeOrgBrand(
            moveMenu?.item?.OrgBrand || moveMenu?.item?.orgBrand,
          );
          return (
            <MenuItem
              key={brand}
              disabled={current === brand}
              onClick={() => {
                const payload = moveMenu;
                setMoveMenu(null);
                if (!payload) return;
                onMoveOrgBrand?.(payload.kind, payload.item, brand);
              }}
            >
              <ListItemIcon>
                <SwapHorizOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t("skillAgent.tree.moveToBrand", {
                  brand,
                  defaultValue: `Mover a ${brand}`,
                })}
              />
            </MenuItem>
          );
        })}
      </Menu>

      <Menu
        open={Boolean(projectMenu)}
        onClose={() => setProjectMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          projectMenu
            ? { top: projectMenu.mouseY, left: projectMenu.mouseX }
            : undefined
        }
      >
        {(() => {
          const entry = favoriteFromCompassNode(projectMenu?.node);
          const starred =
            entry && isCompassFavorite(favorites, entry.kind, entry.id);
          if (!entry) return null;
          return (
            <MenuItem
              onClick={() => {
                setProjectMenu(null);
                onToggleFavorite?.(entry);
              }}
            >
              <ListItemIcon>
                {starred ? (
                  <StarOutlinedIcon
                    fontSize="small"
                    sx={{ color: favAccent }}
                  />
                ) : (
                  <StarBorderOutlinedIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  starred
                    ? t("skillAgent.tree.removeFavorite", {
                        defaultValue: "Quitar de Favoritos",
                      })
                    : t("skillAgent.tree.addFavorite", {
                        defaultValue: "Añadir a Favoritos",
                      })
                }
              />
            </MenuItem>
          );
        })()}
        {(() => {
          const n = projectMenu?.node;
          const missing = n && !String(n.Temporada || "").trim();
          return (
            <MenuItem
              onClick={() => {
                setProjectMenu(null);
                if (n) onEditProjectSeason?.(n);
              }}
            >
              <ListItemIcon>
                {missing ? (
                  <CalendarMonthOutlinedIcon
                    fontSize="small"
                    sx={{ color: "#00897b" }}
                  />
                ) : (
                  <EditOutlinedIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  missing
                    ? t("skillAgent.tree.editProjectMissingSeason", {
                        defaultValue: "Asignar temporada",
                      })
                    : t("skillAgent.tree.editProject", {
                        defaultValue: "Editar proyecto",
                      })
                }
                secondary={
                  missing
                    ? t("skillAgent.tree.editProjectMissingSeasonHint", {
                        defaultValue: "Está en Sin temporada",
                      })
                    : t("skillAgent.tree.editProjectHint", {
                        defaultValue: "Nombre y temporada",
                      })
                }
              />
            </MenuItem>
          );
        })()}
        <MenuItem
          onClick={() => {
            const n = projectMenu?.node;
            setProjectMenu(null);
            if (n) onDeleteProject?.(n);
          }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" sx={{ color: "#c62828" }} />
          </ListItemIcon>
          <ListItemText
            primary={t("skillAgent.tree.deleteProject", {
              defaultValue: "Eliminar proyecto",
            })}
            primaryTypographyProps={{ sx: { color: "#c62828" } }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
}
